// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * Tenant Configuration Module
 *
 * This module provides centralized tenant-specific configuration for multi-tenancy support.
 * All domain-specific URLs and identifiers should be derived from this configuration
 * rather than being hardcoded throughout the codebase.
 *
 * Configuration can be customized via environment variables:
 * - VITE_TENANT_DOMAIN: Primary domain (default: 'nuloucarepoint.com')
 * - VITE_API_DOMAIN: API domain (default: 'api.{VITE_TENANT_DOMAIN}')
 * - VITE_MEDPLUM_BASE_URL: Medplum API base URL
 *
 * Usage:
 * ```ts
 * import { getTenantConfig, ext, ns, cs } from '@/config/tenant';
 *
 * // Get full config
 * const config = getTenantConfig();
 * console.log(config.extensionBaseUrl); // http://nuloucarepoint.com/fhir/StructureDefinition
 *
 * // Use convenience functions for common URL patterns
 * const logoUrl = ext('organization-logo'); // http://nuloucarepoint.com/fhir/StructureDefinition/organization-logo
 * const npiSystem = ns('us-npi'); // http://nuloucarepoint.com/fhir/NamingSystem/us-npi
 * const specialtyCode = cs('africa-specialty'); // http://nuloucarepoint.com/fhir/CodeSystem/africa-specialty
 * ```
 */

/**
 * Tenant configuration interface
 */
export interface TenantConfig {
  // Domain settings
  /** Primary domain (e.g., 'nuloucarepoint.com') */
  domain: string;
  /** API subdomain (e.g., 'api.nuloucarepoint.com') */
  apiDomain: string;

  // FHIR namespace URLs
  /** Base URL for FHIR StructureDefinition extensions */
  extensionBaseUrl: string;
  /** Base URL for FHIR NamingSystem identifiers */
  namingSystemBaseUrl: string;
  /** Base URL for FHIR CodeSystem definitions */
  codeSystemBaseUrl: string;
  /** Base URL for FHIR ValueSet definitions */
  valueSetBaseUrl: string;

  // Bot identifiers
  /** Identifier system for bot lookups */
  botIdentifierSystem: string;

  // AI/messaging systems
  /** Identifier system for AI messages */
  aiMessageSystem: string;
  /** Identifier system for bot references (alternative) */
  botReferenceSystem: string;

  // API base URL
  /** Medplum API base URL */
  medplumBaseUrl: string;
}

// Cache the config to avoid recalculating on every call
let cachedConfig: TenantConfig | null = null;

/**
 * Get the tenant configuration
 *
 * Configuration is derived from environment variables with sensible defaults.
 * The config is cached after first call for performance.
 *
 * @returns TenantConfig object with all tenant-specific URLs and settings
 */
export function getTenantConfig(): TenantConfig {
  if (cachedConfig) {
    return cachedConfig;
  }

  const domain = import.meta.env.VITE_TENANT_DOMAIN || 'nuloucarepoint.com';
  const apiDomain = import.meta.env.VITE_API_DOMAIN || `api.${domain}`;

  cachedConfig = {
    domain,
    apiDomain,
    extensionBaseUrl: `http://${domain}/fhir/StructureDefinition`,
    namingSystemBaseUrl: `http://${domain}/fhir/NamingSystem`,
    codeSystemBaseUrl: `http://${domain}/fhir/CodeSystem`,
    valueSetBaseUrl: `http://${domain}/fhir/ValueSet`,
    botIdentifierSystem: `https://${domain}/bot-identifier`,
    aiMessageSystem: `https://${apiDomain}/ai-message`,
    botReferenceSystem: `https://${apiDomain}/bots`,
    medplumBaseUrl: import.meta.env.VITE_MEDPLUM_BASE_URL || `https://${apiDomain}`,
  };

  return cachedConfig;
}

/**
 * Clear the cached config (useful for testing)
 */
export function clearTenantConfigCache(): void {
  cachedConfig = null;
}

// =============================================================================
// Convenience functions for common URL patterns
// =============================================================================

/**
 * Generate a FHIR StructureDefinition extension URL
 *
 * @param name - Extension name (e.g., 'organization-logo', 'ai-notes-settings')
 * @returns Full extension URL (e.g., 'http://nuloucarepoint.com/fhir/StructureDefinition/organization-logo')
 *
 * @example
 * ext('organization-logo') // 'http://nuloucarepoint.com/fhir/StructureDefinition/organization-logo'
 * ext('ai-notes-settings') // 'http://nuloucarepoint.com/fhir/StructureDefinition/ai-notes-settings'
 */
export const ext = (name: string): string => `${getTenantConfig().extensionBaseUrl}/${name}`;

/**
 * Generate a FHIR StructureDefinition extension URL with HTTPS
 * Use this for extensions that require HTTPS (some newer patterns use https)
 *
 * @param name - Extension name
 * @returns Full extension URL with https scheme
 */
export const extHttps = (name: string): string => {
  const config = getTenantConfig();
  return `https://${config.domain}/fhir/StructureDefinition/${name}`;
};

/**
 * Generate a FHIR NamingSystem URL
 *
 * @param name - NamingSystem name (e.g., 'us-npi', 'africa-medical-council')
 * @returns Full NamingSystem URL
 *
 * @example
 * ns('us-npi') // 'http://nuloucarepoint.com/fhir/NamingSystem/us-npi'
 * ns('ng-mdcn') // 'http://nuloucarepoint.com/fhir/NamingSystem/ng-mdcn'
 */
export const ns = (name: string): string => `${getTenantConfig().namingSystemBaseUrl}/${name}`;

/**
 * Generate a FHIR CodeSystem URL
 *
 * @param name - CodeSystem name (e.g., 'africa-specialty', 'caribbean-specialty')
 * @returns Full CodeSystem URL
 *
 * @example
 * cs('africa-specialty') // 'http://nuloucarepoint.com/fhir/CodeSystem/africa-specialty'
 */
export const cs = (name: string): string => `${getTenantConfig().codeSystemBaseUrl}/${name}`;

/**
 * Generate a FHIR ValueSet URL
 *
 * @param name - ValueSet name (e.g., 'practitioner-role-snomed')
 * @returns Full ValueSet URL
 *
 * @example
 * vs('practitioner-role-snomed') // 'http://nuloucarepoint.com/fhir/ValueSet/practitioner-role-snomed'
 */
export const vs = (name: string): string => `${getTenantConfig().valueSetBaseUrl}/${name}`;

/**
 * Get the bot identifier for a bot lookup
 *
 * @param botName - Bot name (e.g., 'ai-notes-transcribe', 'ai-notes-presigned-url')
 * @returns Identifier string in format 'system|value' for Medplum search
 *
 * @example
 * botIdentifier('ai-notes-transcribe') // 'https://nuloucarepoint.com/bot-identifier|ai-notes-transcribe'
 */
export const botIdentifier = (botName: string): string =>
  `${getTenantConfig().botIdentifierSystem}|${botName}`;

/**
 * Get the AI message identifier
 *
 * @param suffix - Optional suffix for the identifier (e.g., 'topic')
 * @returns AI message system URL with optional suffix
 *
 * @example
 * aiMessageId() // 'https://api.nuloucarepoint.com/ai-message'
 * aiMessageId('topic') // 'https://api.nuloucarepoint.com/ai-message/topic'
 */
export const aiMessageId = (suffix?: string): string => {
  const base = getTenantConfig().aiMessageSystem;
  return suffix ? `${base}/${suffix}` : base;
};
